// @ts-nocheck
import { Badge, Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { useMemo, useRef, useState, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';

import { enrichSingleInstrumentOutput, getReportingAnchorPeriod } from 'redux/DashboardSliceV2';
import { getBlocks } from 'redux/ModelsSlice';
import {
  type ReportingWindow,
  formatInstrumentBadge,
  formatInstrumentBadgeShort,
} from 'utils/reportingPeriodUtils';

import EditableTitle from '../../EditableTitle';

import { KpiInstrument } from './KpiInstrument';
import { ChartInstrument } from './ChartInstrument';
import { TableInstrument } from './TableInstrument';
import { VarianceInstrument } from './VarianceInstrument';
import { BaseInstrumentProperties } from './proptypes';

const ExportInstruments = ({ widget, output }: BaseInstrumentProperties) => {
  const parentReference = useRef<HTMLDivElement>(null);
  const [parentWidth, setParentWidth] = useState(0);
  const anchorPeriod = useSelector(getReportingAnchorPeriod);
  const model = useSelector(getBlocks);
  const fyStartMonth: string =
    (model?.time_properties?.fy_start_month as string | undefined) ?? 'Jan';

  // Match dashboard card behavior
  const BADGE_SHORT_THRESHOLD = 300;
  const BADGE_HIDE_THRESHOLD = 160;

  const windowMode: string | undefined = widget?.formatting?.reporting_window_mode;
  const windowOverride: ReportingWindow | undefined = widget?.formatting?.reporting_window_override;
  const hasWindowOverride = windowMode === 'OVERRIDE' && !!windowOverride;
  const overrideBadgeLabel = hasWindowOverride
    ? parentWidth < BADGE_HIDE_THRESHOLD
      ? null
      : parentWidth < BADGE_SHORT_THRESHOLD
      ? formatInstrumentBadgeShort(anchorPeriod, windowOverride!, fyStartMonth)
      : formatInstrumentBadge(anchorPeriod, windowOverride!, fyStartMonth)
    : null;

  const enrichedOutput = useMemo(() => {
    if (!output || !Array.isArray(output?.data)) return output;

    const indicatorIdToName: Record<number, string> = {};
    const widgetIndicators: any[] = Array.isArray(widget?.instrument_indicators)
      ? widget.instrument_indicators
      : [];

    widgetIndicators.forEach((ind: any) => {
      const id = ind?.indicator_id ?? ind?.id;
      const name = ind?.indicator_name ?? ind?.name;
      if (id != null && name != null) indicatorIdToName[Number(id)] = String(name);
    });

    return enrichSingleInstrumentOutput(output, indicatorIdToName);
  }, [output, widget?.instrument_indicators]);

  useLayoutEffect(() => {
    const updateWidth = () => {
      if (parentReference.current) {
        setParentWidth(parentReference.current.offsetWidth);
      }
    };

    updateWidth();
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (parentReference.current) {
      resizeObserver.observe(parentReference.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const renderInstrument = () => {
    switch (widget?.instrument_type) {
      case 'kpi':
        return <KpiInstrument widget={widget} output={enrichedOutput} />;
      case 'line':
      case 'bar':
      case 'stack':
        return <ChartInstrument widget={widget} output={enrichedOutput} />;
      case 'table':
        return <TableInstrument widget={widget} output={enrichedOutput} />;
      case 'variance':
        return <VarianceInstrument widget={widget} output={enrichedOutput} />;
      default:
        return null;
    }
  };

  return (
    <Box height='100%' padding='0.6rem' width='100%'>
      <Flex justifyContent='space-between' ref={parentReference}>
        <Flex flex='1' minW='0' alignItems='center' gap={2}>
          <Box minW='0' sx={{ p: { minH: 'unset' } }}>
            <EditableTitle
              value={widget?.name}
              handleChange={() => {}} // Read-only in export view
              isEditable={false}
              width='100%'
            />
          </Box>
          {overrideBadgeLabel && (
            <Badge
              colorScheme='purple'
              fontSize='10px'
              px={1.5}
              py={0.5}
              borderRadius='4px'
              whiteSpace='nowrap'
              flexShrink={0}
              alignSelf='center'
            >
              {overrideBadgeLabel}
            </Badge>
          )}
        </Flex>
      </Flex>
      <Box height='calc(100% - 30px)' position='relative' className='scroll' overflow='auto'>
        {enrichedOutput?.error === true ? (
          <Flex
            height='100%'
            alignItems='center'
            justifyContent='center'
            flexDirection='column'
            gap={1}
            px={3}
          >
            <Tooltip label={enrichedOutput?.message || 'An error occurred'} placement='top'>
              <Text
                fontSize='sm'
                color='red.400'
                textAlign='center'
                cursor='default'
                userSelect='none'
              >
                ⚠ Something went wrong
              </Text>
            </Tooltip>
          </Flex>
        ) : (
          renderInstrument()
        )}
      </Box>
    </Box>
  );
};

export default ExportInstruments;
